<script setup>
/**
 * Slack.
 *
 * The connect card, then the part that only Slack needs: a workspace can be
 * linked without a channel picked yet, which is the one state where the
 * integration is on but nothing is being mirrored - so the warning about it
 * sits directly under the card, above the help text naming the live channel.
 *
 * OAuth hands back `?code=`; it is exchanged once and stripped from the URL,
 * because a reload would replay a spent code and fail.
 */
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Integration from './Integration.vue';
import SelectChannelWarning from './Slack/SelectChannelWarning.vue';
import SlackIntegrationHelpText from './Slack/SlackIntegrationHelpText.vue';

const props = defineProps({
  code: { type: String, default: '' },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const integrationLoaded = ref(false);

const integration = computed(() => {
  return store.getters['integrations/getIntegration']('slack');
});

const uiFlags = computed(() => store.getters['integrations/getUIFlags']);

const isLoading = computed(
  () => !integrationLoaded.value || uiFlags.value.isCreatingSlack
);

const areHooksAvailable = computed(() => {
  const { hooks = [] } = integration.value || {};
  return !!hooks.length;
});

const hook = computed(() => {
  const { hooks = [] } = integration.value || {};
  const [firstHook] = hooks;
  return firstHook || {};
});

const isIntegrationHookEnabled = computed(() => {
  return hook.value.status || false;
});

const hasConnectedAChannel = computed(() => {
  return !!hook.value.reference_id;
});

const selectedChannelName = computed(() => {
  if (hook.value.status) {
    const { settings: { channel_name: channelName = '' } = {} } = hook.value;
    return channelName || 'customer-conversations';
  }
  return t('INTEGRATION_SETTINGS.SLACK.HELP_TEXT.SELECTED');
});

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return integration.value.action;
});

const intializeSlackIntegration = async () => {
  await store.dispatch('integrations/get', 'slack');
  if (props.code) {
    await store.dispatch('integrations/connectSlack', props.code);
    // Clear the query param `code` from the URL as the
    // subsequent reloads would result in an error
    router.replace(route.path);
  }
  integrationLoaded.value = true;
};

onMounted(() => {
  intializeSlackIntegration();
});
</script>

<template>
  <SettingsPageShell :title="$t('INTEGRATION_SETTINGS.SLACK.HEADER')">
    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.LOADING')"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <template v-else>
      <Integration
        :integration-id="integration.id"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="integrationAction"
        :action-button-text="$t('INTEGRATION_SETTINGS.SLACK.DELETE')"
        :delete-confirmation-text="{
          title: $t('INTEGRATION_SETTINGS.SLACK.DELETE_CONFIRMATION.TITLE'),
          message: $t('INTEGRATION_SETTINGS.SLACK.DELETE_CONFIRMATION.MESSAGE'),
        }"
      />

      <template v-if="areHooksAvailable">
        <SelectChannelWarning
          v-if="!isIntegrationHookEnabled"
          :has-connected-a-channel="hasConnectedAChannel"
        />
        <SlackIntegrationHelpText
          :selected-channel-name="selectedChannelName"
        />
      </template>
    </template>
  </SettingsPageShell>
</template>
